import { Icon } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { CardTypes, IPaymentMethod } from 'features/payment/types';
import { isPayPal, isVenmo } from 'features/payment/utils';
import { StyleSheet, View } from 'react-native';

import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { primitive, spacing } from 'styles/constants';
import { Region } from 'utils/environment/types';

type SelectorAvailablePaymentsProps = {
  paymentMethods: IPaymentMethod[];
  isReloadPrepaidBeingShown?: boolean;
};
export function SelectorAvailablePayments({
  paymentMethods,
  isReloadPrepaidBeingShown,
}: SelectorAvailablePaymentsProps) {
  const { region } = useLocale();
  const isCARegion = region === Region.CA;
  const { canUseApplePay, canUseGooglePay } = usePaymentContext();
  const enableCashApp = useFlag(LaunchDarklyFlag.ENABLE_CASH_APP_PAYMENTS);

  if (!paymentMethods) {
    return null;
  }

  const availablePayments = [
    {
      title: CardTypes.GIFT_CARD,
      variant: 'smallGiftCardWhite',
      canBeUsed: true,
    },
    {
      title: CardTypes.CREDIT,
      variant: 'smallCreditCardWhite',
      canBeUsed: true,
    },
    {
      title: CardTypes.PAYPAL,
      variant: 'paypalNoBorder',
      canBeUsed:
        !isCARegion &&
        !isReloadPrepaidBeingShown &&
        paymentMethods.some(method => isPayPal(method.accountIdentifier)),
    },
    {
      title: CardTypes.VENMO,
      variant: 'venmoNoBorder',
      canBeUsed:
        !isCARegion &&
        !isReloadPrepaidBeingShown &&
        paymentMethods.some(method => isVenmo(method.accountIdentifier)),
    },
    {
      title: CardTypes.APPLE_PAY,
      variant: 'applepayNoBorder',
      canBeUsed: canUseApplePay,
    },
    {
      title: CardTypes.GOOGLE_PAY,
      variant: 'googlePayBrand',
      canBeUsed: canUseGooglePay,
    },
    {
      title: CardTypes.CASHAPP,
      variant: 'cashAppWhiteBorder',
      canBeUsed: !isCARegion && !isReloadPrepaidBeingShown && enableCashApp,
    },
  ];

  return (
    <View style={styles.bottomSection}>
      {availablePayments
        .filter(({ canBeUsed }) => canBeUsed === true)
        .map(({ title, variant }) => (
          <Icon key={title} accessibilityHidden title={title} variant={variant} size="md" />
        ))}
    </View>
  );
}

const styles = StyleSheet.create({
  bottomSection: {
    display: 'flex',
    flexDirection: 'row',
    height: 32,
    width: '100%',
    gap: spacing.$2,
    backgroundColor: primitive.$lightBrown,
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: spacing.$4,
  },
});
